// A leaf module on purpose: MapBoxViewer.store drags in turf and the event store, and the
// federated viewer needs these numbers without that graph.

// At or below this, a map shows the project address marker and nothing else.
export const PROJECT_MARKER_ZOOM_LEVEL = 10

export const MAX_ZOOM_LEVEL = 22
export const MAX_PITCH_LEVEL = 60

// A hair above the camera's ceiling so nothing culls at max zoom.
export const MAX_ITEM_ZOOM_LEVEL = MAX_ZOOM_LEVEL + 0.1

// Where a map lands when it has no saved zoom of its own.
export const DEFAULT_ZOOM_VALUE = 18

// The one zoom a Precise Location is framed at, on every surface that shows it. A camera on the
// record means the user chose it.
export const preciseLocationZoom = (zoom?: number | null): number =>
  zoom ?? DEFAULT_ZOOM_VALUE

// Viewers read this, never the number.
export const isProjectMarkerOnlyZoom = (zoom: number): boolean =>
  zoom <= PROJECT_MARKER_ZOOM_LEVEL

// The ramp every scaling thing on the map shares: markers, workflow pins, and the labels that
// name them. It lives here, in the leaf, because it is a property of the CAMERA rather than of
// any one family of artwork — and because the label styling module and the marker spec both need
// it, and the marker spec already imports the label styling module. One home, no cycle.
export const ZOOM_SCALE_MIN = 10
export const ZOOM_SCALE_MAX = 17

// How far the whole marker shrinks by the bottom of the ramp — location markers here and workflow
// pins through WorkflowPinLayer, which shares this so the two families shrink in lockstep.
//
// Held at the DOM's value so a migrated marker is the size it was. Raising it would flatten the
// ramp and bound how wrong a boxed label looks mid-fling, but it scales everything with it and
// costs that parity.
export const ZOOM_SCALE_MIN_FACTOR = 0.15

export function getZoomScale(zoom: number): number {
  const t = Math.max(
    0,
    Math.min(1, (zoom - ZOOM_SCALE_MIN) / (ZOOM_SCALE_MAX - ZOOM_SCALE_MIN)),
  )
  return ZOOM_SCALE_MIN_FACTOR + t * (1 - ZOOM_SCALE_MIN_FACTOR)
}

// getZoomScale as a GL size, ending at `fullSize`. The stops are scaled: GL rejects a zoom
// expression anywhere but the outermost, so it cannot be wrapped in a multiply.
export const zoomScaledSize = (
  isZoomScalingEnabled: boolean,
  fullSize: number,
): unknown =>
  isZoomScalingEnabled
    ? [
        'interpolate',
        ['linear'],
        ['zoom'],
        ZOOM_SCALE_MIN,
        fullSize * ZOOM_SCALE_MIN_FACTOR,
        ZOOM_SCALE_MAX,
        fullSize,
      ]
    : fullSize

// Rasters baked per zoom band.
//
// A 9-patch's fixed material — a corner radius, a company chip — is PIXELS and cannot stretch
// the way its bands can, so the image is baked once per band, picked by a `step` on zoom, and
// icon-size scales the fitted quad the rest of the way. One home for the convention: a raster
// baked against one and picked against another is silently the wrong size.
const IMAGE_BAND_SEPARATOR = '-b'

export const imageBandSuffix = (band: number | null): string =>
  band === null ? '' : `${IMAGE_BAND_SEPARATOR}${band}`

export function bandOfImageId(imageId: string): number | null {
  const at = imageId.lastIndexOf(IMAGE_BAND_SEPARATOR)
  if (at < 0) return null
  const band = Number(imageId.slice(at + IMAGE_BAND_SEPARATOR.length))
  return Number.isFinite(band) ? band : null
}

export const withoutImageBand = (imageId: string): string => {
  const at = imageId.lastIndexOf(IMAGE_BAND_SEPARATOR)
  return at >= 0 && bandOfImageId(imageId) !== null
    ? imageId.slice(0, at)
    : imageId
}

// The scale a raster baked for `band` is drawn at. One level AHEAD of the band: mapbox shapes a
// tile's text at floor(zoom) + 1, so baking pre-divided by that level is what lets icon-size
// land the result on the label's own curve. Bandless art is drawn at full size.
export const bakedScaleForBand = (band: number | null): number =>
  band === null ? 1 : getZoomScale(band + 1)

// Every band a ramped raster needs, for building the `step` that picks between them.
export const IMAGE_BANDS: number[] = Array.from(
  { length: ZOOM_SCALE_MAX - ZOOM_SCALE_MIN + 1 },
  (_, i) => ZOOM_SCALE_MIN + i,
)
